'use client';

import {useEffect, useState} from "react";
import {CircularProgress, Grid2, Stack, Typography} from "@mui/material";
import KbNav from "@/src/components/common/knowledge-base/nav/kb-nav";
import LastUpdate from "@/src/components/common/pages/last-update/last-update";
import CustomDivider from "@/src/mui-custom/custom-divider/custom-divider";
import PageContent from "@/src/components/journal-site/common/page-content/page-content";
import ReactHtmlParser from "react-html-parser";
import useSWR from "swr";
import {fetcher} from "@/src/utils/fetch-helpers";
import Loading from "@/src/components/common/loading/loading";

export default function KnowledgeBase({
                                          initialJournalData,
                                          initialKbTreeData,
                                          journalTitle,
                                      }: {
    initialJournalData: any;
    initialKbTreeData: any;
    journalTitle: string;
}) {

    const {
        data: journalResponse,
        isLoading: isJournalLoading
    } = useSWR(`/api/journals?path=${journalTitle}`, fetcher, {
        fallbackData: {success: true, data: [initialJournalData]},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const journal = Array.isArray(journalResponse?.data) ? journalResponse.data[0] : journalResponse?.data;


    const {
        data: kbTreeResponse,
        isLoading: isKbLoading
    } = useSWR(`/api/kb-tree?journalId=${journal?.id}`, fetcher, {
        fallbackData: {success: true, data: initialKbTreeData},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const kbTree = Array.isArray(kbTreeResponse?.data) ? kbTreeResponse.data[0] : kbTreeResponse?.data;

    if (isJournalLoading || isKbLoading) {
        return (
            <Loading/>
        );
    }

    const [selectedSlug, setSelectedSlug] = useState('');
    const [kbContent, setKbContent] = useState<any>(null);
    const [loadingContent, setLoadingContent] = useState(false);

    useEffect(() => {
        if (selectedSlug) {
            const handleHashChange = async () => {
                setLoadingContent(true);
                try {
                    const response = await fetch(`/api/kb?slug=${selectedSlug}`);
                    if (!response.ok) throw new Error("Failed to fetch content");

                    const body = await response.json();
                    setKbContent(body.data)
                } catch (error) {
                    console.error("Error fetching content:", error);
                    setKbContent("<p>Failed to load content.</p>");
                }
                setLoadingContent(false);
            };
            handleHashChange();
        }
    }, [selectedSlug]);

    return (
        <Grid2 container spacing={2}
               sx={{
                   py: 2,
                   p: {
                       'md': 5,
                       'lg': 4,
                   },
                   m: 'auto',
                   width: '100%',
                   justifyContent: 'center',
               }}>
            <Grid2 size={{lg: 3, md: 12}}>
                <KbNav kbTree={kbTree} setSelectedSlug={setSelectedSlug} selectedSlug={selectedSlug}/>
            </Grid2>
            <Grid2 size={{lg: 9, md: 12}}>
                {!kbContent && (
                    <>
                        <Typography
                            component={'h1'}
                            fontSize={{
                                xs: '1.1rem',
                                sm: '1.2rem',
                                md: '1.5rem'
                            }}
                            marginBottom={{
                                xs: '1rem',
                                sm: '1rem',
                                md: '0rem'
                            }}
                            color={"primary"}
                        >
                            Knowledge Base
                        </Typography>
                        <Typography variant={"fs6"} component={'p'}>
                            Please Select one of knowledge bases.
                        </Typography>
                    </>
                )}
                {loadingContent && (
                    <CircularProgress sx={{margin: 'auto', display: 'block'}}/>
                )}
                {kbContent && !loadingContent && (
                    <>
                        {
                            kbContent?.title &&
                            <Typography
                                component={'h1'}
                                fontSize={{
                                    xs: '1.2rem',
                                    sm: '1.3rem',
                                    md: '1.75rem'
                                }}
                                marginBottom={{
                                    xs: '1rem',
                                    sm: '1rem',
                                    md: '0rem'
                                }}
                                color={"primary"}
                            >
                                {kbContent.title}
                            </Typography>
                        }
                        <Stack
                            flexDirection={{
                                xs: "column",
                                sm: "column",
                                md: "row",
                            }}
                            alignItems={{
                                xs: "center",
                                sm: "center",
                                md: "right",
                            }}
                            justifyContent={{
                                xs: "center",
                                sm: "center",
                                md: "right",
                            }}
                            mt={1}
                            gap={1.5}
                        >
                            {
                                kbContent?.updated_at &&
                                <LastUpdate updatedAt={kbContent.updated_at}/>
                            }
                        </Stack>
                        <CustomDivider sx={{marginY: "1rem", marginTop: {lg: "1rem"}}}/>
                        <PageContent>
                            {ReactHtmlParser(kbContent.body)}
                        </PageContent>
                    </>
                )}
            </Grid2>
        </Grid2>
    );
}